import React from 'react';
import styled, { css } from 'styled-components';
import { useFormikContext, useField } from 'formik';
import isNil from 'lodash/isNil';

import { ITextarea, IStyledTextarea } from './types';
import { StyledInputContainer, InputStyles } from '../styled';
import InputHeader from '../InputHeader';
import InputError from '../InputError';
import InputHint from '../InputHint';
import PostfixIconWrapper from '../PostfixIconWrapper';
import { Done } from '@icons/common';

const Textarea: React.FC<ITextarea> = props => {
  const {
    className,
    name,
    placeholder = '',
    nativeInputProps = {},
    header = '',
    prependIcon,
    isMobile = false,
    hint,
    onChange,
    ...formikRest
  } = props;

  const [field] = useField({ name, ...formikRest });
  const { onChange: onChangeFormik, onBlur } = field;
  const { setFieldValue, errors, touched } = useFormikContext();

  const [focus, setFocus] = React.useState(false);

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChangeFormik(e.target.value);
    onChange?.(e.target.value);
    setFieldValue(name, e.target.value);
  };

  const hasError = Boolean(errors[name] && touched[name]);
  const styledProps: IStyledTextarea = {
    hasError: hasError && !focus,
    hasTouched: Boolean(touched[name]),
  };

  const inputProps: any = {
    ...field,
    ...nativeInputProps,
    ...styledProps,
    id: name,
    name,
    placeholder,
    onChange: onChangeHandler,
    onBlur: e => {
      setFocus(false);
      onBlur(e);
    },
    onFocus: () => setFocus(true),
  };
  const isCompleted =
    Boolean(!errors[name]) && Boolean(touched[name]) && Boolean(field.value);
  const isShowSuccessIcon = !focus && isCompleted;

  return (
    <StyledInputContainer className={className}>
      <InputHeader
        text={header}
        prependIcon={prependIcon}
        isMobile={isMobile}
      />
      <StyledTextareaWrapper {...styledProps}>
        <StyledTextarea {...inputProps} />
        {isShowSuccessIcon && (
          <StyledPostfixIconWrapper>
            <DoneStyled />
          </StyledPostfixIconWrapper>
        )}
      </StyledTextareaWrapper>
      <InputError name={name} />
      {!isNil(hint) && !hasError && <InputHint>{hint}</InputHint>}
    </StyledInputContainer>
  );
};

const StyledPostfixIconWrapper = styled(PostfixIconWrapper)`
  position: absolute;
  right: 8px;
  top: 8px;
  padding: 0;
`;

const StyledTextareaWrapper = styled.div<IStyledTextarea>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  overflow: hidden;
  ${InputStyles.Normal}
  ${({ disabled }) => disabled && InputStyles.Disabled};
  ${({ hasError }) => hasError && InputStyles.Error};
`;

const StyledInputCSS = css<IStyledTextarea>`
  ${InputStyles.Base}
  width: 100%;
  height: 112px;
  padding: 16px 28px 16px 8px;
  ::placeholder {
    ${InputStyles.Placeholder}
  }

  :-webkit-autofill {
    -webkit-box-shadow: 0 0 0 1000px white inset !important;
  }
  :focus {
    ${InputStyles.Focus}
  }
`;

const StyledTextarea = styled.textarea<IStyledTextarea>`
  ${StyledInputCSS}
`;

const DoneStyled = styled(Done)`
  path {
    stroke: #459e18;
  }
`;

export default Textarea;
